import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Notion-style resizable table columns with widths persisted per table.
 */
export function useColumnWidths<K extends string>(
  storageKey: string,
  defaults: Record<K, number>,
  opts?: { min?: number; max?: number }
) {
  const min = opts?.min ?? 80;
  const max = opts?.max ?? 800;

  const [widths, setWidths] = useState<Record<K, number>>(() => {
    if (typeof window === "undefined") return defaults;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return defaults;
      const parsed = JSON.parse(raw) as Partial<Record<K, number>>;
      return { ...defaults, ...parsed };
    } catch {
      return defaults;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch {
      /* ignore quota errors */
    }
  }, [storageKey, widths]);

  const drag = useRef<{ key: K; startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      const next = Math.min(max, Math.max(min, d.startWidth + (e.clientX - d.startX)));
      setWidths((w) => ({ ...w, [d.key]: next }));
    };
    const onUp = () => {
      if (!drag.current) return;
      drag.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [min, max]);

  const startResize = useCallback(
    (key: K, e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      drag.current = { key, startX: e.clientX, startWidth: widths[key] };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [widths]
  );

  const reset = useCallback(() => setWidths(defaults), [defaults]);

  return { widths, startResize, reset };
}

/** Drag handle rendered inside a <TableHead>. */
export function ColumnResizer({ onPointerDown }: { onPointerDown: (e: React.PointerEvent) => void }) {
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      onPointerDown={onPointerDown}
      onClick={(e) => e.stopPropagation()}
      className="absolute right-0 top-0 h-full w-2 translate-x-1/2 cursor-col-resize select-none touch-none group/resizer z-20"
    >
      <span className="block h-full w-px mx-auto bg-transparent group-hover/resizer:bg-primary group-hover/resizer:w-0.5 transition-all" />
    </span>
  );
}
